import React, { useState } from "react";
import Section from "./Section";
import { X_PADDING } from "../constants";
import Page from "./Page";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

const AssociateMember = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (index) => {
    setOpenSection(openSection === index ? null : index);
  };

  return (
    <Page name="Associate Membership">
      <div className={`w-full bg-white pt-8 pb-16 ${X_PADDING}`}>
        <Section
        name="Associate"
        description=" Associate Membership is designed for Clinical Officers who are not
              currently engaged in active clinical practice or those practicing
              outside Zambia. Stay connected with the profession and enjoy
              select benefits"/>
        <div className="w-full mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            
            <div className="w-16 h-1 bg-[#1477c2] mx-auto mt-0 rounded-full"></div>
          </div>

          {/* Two Cards Grid - Spaced out with max-width */}
          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Card 1 - Eligibility Criteria */}
              <CurtainCard
                title="Eligibility Criteria"
                index={0}
                isOpen={openSection === 0}
                onToggle={toggleSection}
                items={ELIGIBILITY_ITEMS}
                gradient="from-[#1477c2] to-[#1b9de3]"
              />

              {/* Card 2 - Rights and Privileges */}
              <CurtainCard
                title="Rights and Privileges"
                index={1}
                isOpen={openSection === 1}
                onToggle={toggleSection}
                items={RIGHTS_ITEMS}
                gradient="from-[#16365c] to-[#1477c2]"
              />
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default AssociateMember;

const RenderItem = ({ item, depth = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  const toggleChildren = () => {
    if (hasChildren) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="flex flex-col">
      <div
        className={`flex items-start gap-3 text-sm text-[#16365c] leading-relaxed ${hasChildren ? "cursor-pointer hover:text-[#1477c2]" : ""}`}
        onClick={toggleChildren}
        style={{ paddingLeft: depth > 0 ? `${depth * 24}px` : "0" }}
      >
        <span className="text-[#1477c2] font-bold text-lg leading-none mt-0.5 flex-shrink-0">
          •
        </span>
        <span className={hasChildren ? "font-semibold" : ""}>
          {item.text}
          {hasChildren && (
            <span className="ml-2 text-[#1477c2]">
              {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
            </span>
          )}
        </span>
      </div>

      {hasChildren && isOpen && (
        <div className="mt-1 space-y-1">
          {item.children.map((child, idx) => (
            <RenderItem key={idx} item={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

// Curtain Card Component
const CurtainCard = ({ title, index, isOpen, onToggle, items, gradient }) => {
  return (
    <div className="rounded-xl shadow-md overflow-hidden border border-[#eef1f4] bg-white hover:shadow-lg transition-shadow duration-300 h-fit">
      <div className="relative">
        {/* Curtain - Blue gradient section with sliding animation */}
        <div
          className={`
            bg-gradient-to-br ${gradient} relative overflow-hidden cursor-pointer
            transition-all duration-700 ease-in-out
            ${isOpen ? "h-[50px]" : "h-[180px]"}
          `}
          onClick={() => onToggle(index)}
        >
          {/* Decorative pattern - subtle dots */}
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern
                  id={`dots-${index}`}
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <circle cx="2" cy="2" r="1.5" fill="white" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill={`url(#dots-${index})`} />
            </svg>
          </div>

          {/* Decorative diagonal lines */}
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern
                  id={`lines-${index}`}
                  x="0"
                  y="0"
                  width="30"
                  height="30"
                  patternUnits="userSpaceOnUse"
                  patternTransform="rotate(45)"
                >
                  <line
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="30"
                    stroke="white"
                    strokeWidth="2"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill={`url(#lines-${index})`} />
            </svg>
          </div>

          {/* Decorative circles */}
          <div className="absolute -right-6 -top-6 w-16 h-16 rounded-full bg-white/10"></div>
          <div className="absolute -left-4 -bottom-4 w-12 h-12 rounded-full bg-white/5"></div>

          {/* Title */}
          <div className="relative z-10 pt-5 px-5">
            <h3 className="text-xl font-bold text-white">{title}</h3>
          </div>

          {/* Arrow at the bottom of the blue curtain */}
          {!isOpen && (
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-10">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors duration-300 animate-bounce">
                <FaChevronDown className="text-white text-lg" />
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div
          className={`
            overflow-hidden transition-all duration-700 ease-in-out
            ${isOpen ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"}
          `}
        >
          <div className="p-5 bg-white border-t border-[#eef1f4] relative">
            <div className="space-y-2 pb-14">
              {items.map((item, idx) => (
                <RenderItem key={idx} item={item} />
              ))}
            </div>

            {/* Arrow at the bottom of the content */}
            {isOpen && (
              <div
                className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-10 cursor-pointer"
                onClick={() => onToggle(index)}
              >
                <div className="w-10 h-10 rounded-full bg-[#1477c2]/10 flex items-center justify-center hover:bg-[#1477c2]/20 transition-colors duration-300">
                  <FaChevronUp className="text-[#1477c2] text-lg" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Eligibility Criteria Items
const ELIGIBILITY_ITEMS = [
  {
    text: "Are qualified Clinical Officers or mid-level clinical practitioners not currently engaged in active clinical practice.",
    children: [],
  },
  {
    text: "Are Clinical Officers practicing outside the jurisdiction of Zambia.",
    children: [],
  },
  {
    text: "Meet any additional criteria established by the National Executive Committee.",
    children: [],
  },
];

// Rights and Privileges Items
const RIGHTS_ITEMS = [
  {
    text: "Attendance at General Meetings: Associate Members have the right to attend general meetings convened by the Association.",
    children: [],
  },
  {
    text: "Non-voting Participation: While Associate Members may participate in discussions and activities, they shall not have voting rights in Association matters.",
    children: [],
  },
  {
    text: "Access to Designated Resources: Associate Members shall have access to specific resources and materials as determined by the National Executive Committee.",
    children: [],
  },
];